import React, { useEffect, useReducer, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import CloseIcon from '@material-ui/icons/Close'
import { getPortalContainer } from './portal'
import { streamAgent } from './aiApi'
import { documentBlocks, elementBlocks, rangeSelection, rangeWithin } from './aiContent'
import { acceptChanges, addAgentChanges, getChanges, rejectChanges, setAnchors } from './aiReview'
import { loadAiChat, saveAiChat } from './aiChatHistory'
import useStyles from './styles'


const MAX_HISTORY = 20

const suggestions = [
  'Summarize this document',
  'Fix spelling and grammar throughout',
  'Make the tone more formal',
  'Add a short introduction',
  'Which points would a client find unclear?'
]

const readOnlySuggestions = [
  'Summarize this document',
  'What should the client do next?',
  'Which points would a client find unclear?',
  'Find related advices'
]

// The agent can propose edits, but a viewed document can't take them
const READ_ONLY_NOTE = '(I am viewing the document and can\'t edit it here. Answer without changing it.)'

// Blocks containing the selection, as the agent sees them
const selectionForAgent = (editor, blocks) => {
  const { from, to, empty } = editor.state.selection
  const text = empty ? '' : editor.state.doc.textBetween(from, to, '\n').trim()
  if (!text) {
    return null
  }
  const first = blocks.find(block => block.to > from)
  const last = [...blocks].reverse().find(block => block.from < to)
  return first && last ? { from_id: first.id, to_id: last.id, text } : null
}

// Earlier turns, text only, starting with a user message as the API requires
const historyFrom = (messages) => {
  const turns = messages
    .filter(message => !message.error && message.content)
    .map(message => ({ role: message.role, content: message.content }))
    .slice(-MAX_HISTORY)
  const start = turns.findIndex(turn => turn.role === 'user')
  return start < 0 ? [] : turns.slice(start)
}

// Side panel where the user chats with an AI agent that reads the document
// and proposes edits to it, reviewed in the document itself.
// Without an editor it chats about the rendered HTML in contentRef instead,
// read-only. With a chatKey the conversation is kept for the session (see
// aiChatHistory).
const AiChatPanel = ({ editor, contentRef, chatKey, onClose }) => {
  const readOnly = !editor
  const localClasses = useStyles()
  const panelRef = useRef(null)
  const listRef = useRef(null)
  const abortRef = useRef(null)
  const [messages, setMessages] = useState(() => loadAiChat(editor, chatKey))
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  // Read-only: the last selection in the content, which typing here would
  // otherwise move into the input. Taken before the input's autofocus.
  const [contentRange] = useState(() => ({ current: readOnly ? rangeWithin(contentRef.current) : null }))
  const [, refresh] = useReducer(count => count + 1, 0)

  // Pending changes are shown per message, so follow the review state
  useEffect(() => {
    if (!editor) {
      return undefined
    }
    editor.on('transaction', refresh)
    return () => editor.off('transaction', refresh)
  }, [editor])

  useEffect(() => () => abortRef.current && abortRef.current.abort(), [])

  useEffect(() => {
    if (!readOnly) {
      return undefined
    }
    const track = () => {
      const range = rangeWithin(contentRef.current)
      if (range !== undefined) {
        contentRange.current = range
      }
    }
    document.addEventListener('selectionchange', track)
    return () => document.removeEventListener('selectionchange', track)
  }, [readOnly, contentRef, contentRange])

  useEffect(() => {
    saveAiChat(chatKey, messages)
  }, [chatKey, messages])

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [messages])

  const updateLast = update => setMessages(current => [
    ...current.slice(0, -1),
    { ...current[current.length - 1], ...update(current[current.length - 1]) }
  ])

  const send = (text) => {
    const request = text.trim()
    if (!request || busy) {
      return
    }
    const element = contentRef && contentRef.current
    if (readOnly && !element) {
      return
    }
    const blocks = readOnly ? elementBlocks(element) : documentBlocks(editor)
    const selection = readOnly ? rangeSelection(contentRange.current, blocks) : selectionForAgent(editor, blocks)
    const history = historyFrom(messages)
    if (!readOnly) {
      setAnchors(editor.view, blocks)
    }
    setMessages(current => [
      ...current,
      { role: 'user', content: request, selection: selection && selection.text },
      { role: 'assistant', content: '', activity: 'Reading the document…', ...(readOnly ? {} : { changeIds: [] }) }
    ])
    setInput('')
    setBusy(true)

    const controller = new AbortController()
    abortRef.current = controller
    streamAgent({
      history,
      request: readOnly ? `${READ_ONLY_NOTE}\n\n${request}` : request,
      blocks: blocks.map(({ id, html }) => ({ id, html })),
      selection
    }, (event) => {
      if (event.type === 'text') {
        updateLast(message => ({ content: message.content + event.text, activity: null }))
      } else if (event.type === 'activity') {
        updateLast(() => ({ activity: event.label }))
      } else if (event.type === 'changes' && !readOnly) {
        const changeIds = addAgentChanges(editor.view, event.changes)
        updateLast(() => ({ changeIds }))
      } else if (event.type === 'error') {
        updateLast(() => ({ error: event.message }))
      }
    }, controller.signal)
      .catch((error) => {
        if (!controller.signal.aborted) {
          updateLast(() => ({ error: error.message }))
        }
      })
      .then(() => {
        abortRef.current = null
        setBusy(false)
        updateLast(message => ({
          activity: null,
          content: message.content || (controller.signal.aborted ? 'Stopped.' : '')
        }))
      })
  }

  const stop = () => abortRef.current && abortRef.current.abort()

  const pendingIds = new Set(readOnly ? [] : getChanges(editor.view).map(change => change.id))
  const allPending = messages.flatMap(message => (message.changeIds || []).filter(id => pendingIds.has(id)))

  const scrollToChange = (id) => {
    const element = editor.view.dom.querySelector(`[data-ai-change="${id}"]`)
    if (element) {
      element.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  return ReactDOM.createPortal(
    <div className={localClasses.aiPanel} ref={panelRef}>
      <div className={localClasses.aiPanelHeader}>
        <span className={localClasses.aiSparkle}>✦</span>
        <span className={localClasses.aiPanelTitle}>AI assistant</span>
        {
          messages.length > 0 && !busy &&
          <button type='button' className={localClasses.menuButton} onClick={() => setMessages([])}>
            New chat
          </button>
        }
        <button type='button' className={`${localClasses.menuButton} icon`} onClick={onClose} title='Close'>
          <CloseIcon fontSize='small' />
        </button>
      </div>
      <div className={localClasses.aiPanelMessages} ref={listRef}>
        {
          messages.length === 0 &&
          <div className={localClasses.aiPanelEmpty}>
            <div>
              {
                readOnly ?
                'Ask the AI about this document. Select text first to ask about a part of it.' :
                'Ask about this document or tell the AI what to change. Edits are shown in the document for you to accept or reject.'
              }
            </div>
            <div className={localClasses.aiSuggestions}>
              {
                (readOnly ? readOnlySuggestions : suggestions).map(suggestion => (
                  <button key={suggestion} type='button' className={localClasses.aiSuggestion} onClick={() => send(suggestion)}>
                    {suggestion}
                  </button>
                ))
              }
            </div>
          </div>
        }
        {
          messages.map((message, index) => {
            const pending = (message.changeIds || []).filter(id => pendingIds.has(id))
            const total = (message.changeIds || []).length
            return (
              <div key={index} className={`${localClasses.aiMessage} ${message.role}`}>
                {
                  message.selection &&
                  <div className={localClasses.aiMessageQuote}>{message.selection}</div>
                }
                {message.content && <div className={localClasses.aiMessageText}>{message.content}</div>}
                {message.activity && <div className={localClasses.aiStatus}>{message.activity}</div>}
                {message.error && <div className={localClasses.mediaError}>{message.error}</div>}
                {
                  total > 0 &&
                  <div className={localClasses.aiChangeSummary}>
                    {
                      pending.length ?
                      <>
                        <button type='button' className={localClasses.aiLink} onClick={() => scrollToChange(pending[0])}>
                          {pending.length} of {total} {total === 1 ? 'change' : 'changes'} to review
                        </button>
                        <button type='button' className={`${localClasses.menuButton} primary`} onClick={() => acceptChanges(editor.view, pending)}>
                          Accept all
                        </button>
                        <button type='button' className={localClasses.menuButton} onClick={() => rejectChanges(editor.view, pending)}>
                          Reject all
                        </button>
                      </> :
                      <span className={localClasses.aiStatus}>{total === 1 ? 'Change reviewed' : 'All changes reviewed'}</span>
                    }
                  </div>
                }
              </div>
            )
          })
        }
      </div>
      {
        allPending.length > 1 && messages.filter(message => (message.changeIds || []).some(id => pendingIds.has(id))).length > 1 &&
        <div className={localClasses.aiChangeSummary}>
          <span className={localClasses.aiStatus}>{allPending.length} changes to review</span>
          <button type='button' className={`${localClasses.menuButton} primary`} onClick={() => acceptChanges(editor.view, allPending)}>
            Accept all
          </button>
          <button type='button' className={localClasses.menuButton} onClick={() => rejectChanges(editor.view, allPending)}>
            Reject all
          </button>
        </div>
      }
      <form
        className={localClasses.aiPanelInput}
        onSubmit={(event) => {
          event.preventDefault()
          // Keep the submit from bubbling through the portal into a surrounding form
          event.stopPropagation()
          send(input)
        }}
      >
        <textarea
          autoFocus
          rows={2}
          value={input}
          placeholder={busy ? 'AI is working…' : 'Ask AI about this document…'}
          onChange={event => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              send(input)
            } else if (event.key === 'Escape') {
              onClose()
            }
          }}
        />
        {
          busy ?
          <button type='button' className={localClasses.menuButton} onClick={stop}>Stop</button> :
          <button type='submit' className={`${localClasses.menuButton} primary`} disabled={!input.trim()}>Send</button>
        }
      </form>
    </div>,
    readOnly ? document.body : getPortalContainer(editor)
  )
}

export default AiChatPanel
